import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useFetchObjects from "../../../api/useFetchObjects";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import moment from "moment";

export default function EncounterCreate({ open, onClose, ticket, patient }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    ticket: ticket?.id || null,
    patient: patient?.id || null,
    encounter_type: 1, // Consultation
    encounter_date: moment().format("YYYY-MM-DDTHH:mm"),
    department: null,
    provider: null,
    status: 1, // In Progress
    chief_complaint: "",
    diagnosis: "",
    notes: "",
  });
  
  const [errors, setErrors] = useState({});

  // Fetch departments
  const { data: departmentsData } = useFetchObjects(
    ["departments"],
    "departments/?page_size=1000",
    token
  );
  const departments = departmentsData?.results || departmentsData || [];

  // Fetch users (doctors/providers) - filter by doctor=true, optionally by department
  const deptId = formData.department?.id || formData.department || ticket?.department?.id || ticket?.department;
  const usersUrl = deptId
    ? `users/?doctor=true&department=${deptId}&page_size=1000`
    : "users/?doctor=true&page_size=1000";
  const { data: usersData } = useFetchObjects(
    ["users-doctors", deptId],
    usersUrl,
    token
  );
  const users = usersData?.results || usersData || [];

  // Reset form when dialog opens/closes or ticket/patient changes
  useEffect(() => {
    if (open) {
      setFormData({
        ticket: ticket?.id || null,
        patient: patient?.id || null,
        encounter_type: 1,
        encounter_date: moment().format("YYYY-MM-DDTHH:mm"),
        department: ticket?.department || null,
        provider: null,
        status: 1,
        chief_complaint: "",
        diagnosis: "",
        notes: "",
      });
      setErrors({});
    }
  }, [open, ticket, patient]);

  const createEncounter = useMutation({
    mutationFn: async (data) => {
      const payload = {
        ticket: data.ticket || null,
        patient: data.patient,
        encounter_type: data.encounter_type,
        encounter_date: data.encounter_date,
        department: data.department || null,
        provider: data.provider || null,
        status: data.status,
        chief_complaint: data.chief_complaint || null,
        diagnosis: data.diagnosis || null,
        notes: data.notes || null,
      };
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || "http://localhost:9000/api"}/encounters/`,
        payload,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["encounters"]);
      queryClient.invalidateQueries(["ticket"]);
      onClose();
    },
    onError: (err) => {
      if (err.response?.data) {
        setErrors(err.response.data);
      } else {
        setErrors({ non_field_errors: ["Failed to create encounter"] });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.patient) {
      newErrors.patient = "Patient is required";
    }
    if (!formData.encounter_type) {
      newErrors.encounter_type = "Encounter type is required";
    }
    if (!formData.encounter_date) {
      newErrors.encounter_date = "Encounter date is required";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    createEncounter.mutate(formData);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value === "" ? null : value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const encounterTypes = [
    { value: 1, label: "Consultation" },
    { value: 2, label: "Procedure" },
    { value: 3, label: "Admission" },
    { value: 4, label: "Emergency" },
    { value: 5, label: "Follow-up" },
    { value: 6, label: "Diagnostic" },
    { value: 7, label: "Other" },
  ];

  const statusOptions = [
    { value: 1, label: "In Progress" },
    { value: 2, label: "Completed" },
    { value: 3, label: "Cancelled" },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">Create New Encounter</Typography>
            {ticket && (
              <Typography variant="body2" color="text.secondary">
                (Ticket: {ticket.ticket_number})
              </Typography>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {errors.non_field_errors && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.non_field_errors.map((err, idx) => (
                <div key={idx}>{err}</div>
              ))}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Patient (read-only if from ticket) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Patient"
                value={ticket?.patient_name_display || "N/A"}
                disabled
                helperText="Patient is pre-filled from ticket"
              />
            </Grid>

            {/* Encounter Type */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Encounter Type *"
                value={formData.encounter_type}
                onChange={handleChange("encounter_type")}
                error={!!errors.encounter_type}
                helperText={errors.encounter_type}
                required
              >
                {encounterTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Encounter Date */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Encounter Date/Time *"
                value={formData.encounter_date}
                onChange={handleChange("encounter_date")}
                error={!!errors.encounter_date}
                helperText={errors.encounter_date}
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Department */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={formData.department || ""}
                  onChange={(e) => {
                    const dept = departments.find((d) => d.id === e.target.value);
                    setFormData((prev) => ({ ...prev, department: dept?.id || null }));
                  }}
                  label="Department"
                >
                  <MenuItem value="">None</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Provider/Doctor */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Provider/Doctor</InputLabel>
                <Select
                  value={formData.provider || ""}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, provider: e.target.value || null }));
                  }}
                  label="Provider/Doctor"
                >
                  <MenuItem value="">None</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name || ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={handleChange("status")}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Chief Complaint */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Chief Complaint"
                value={formData.chief_complaint}
                onChange={handleChange("chief_complaint")}
                error={!!errors.chief_complaint}
                helperText={errors.chief_complaint}
                placeholder="Patient's main complaint or reason for visit..."
              />
            </Grid>

            {/* Diagnosis */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Diagnosis"
                value={formData.diagnosis}
                onChange={handleChange("diagnosis")}
                error={!!errors.diagnosis}
                helperText={errors.diagnosis}
                placeholder="Clinical diagnosis..."
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={handleChange("notes")}
                error={!!errors.notes}
                helperText={errors.notes}
                placeholder="Additional notes or observations..."
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            startIcon={<CancelIcon />}
            disabled={createEncounter.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={createEncounter.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={createEncounter.isPending}
          >
            {createEncounter.isPending ? "Creating..." : "Create Encounter"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
